import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { navigate, useRoute } from "../route";
import type {
  ReviewArea,
  ReviewFinding,
  ReviewReport,
  ReviewSeverity,
} from "../types";
import { CopyLink } from "./CopyLink";

/**
 * Daily review — what the reviewer agent flagged in one day of prod
 * conversations (iMessage threads and live sessions), one row per finding.
 *
 * Written each morning by the engine's daily job (`bean_engine/reviewer`),
 * which reads the same threads and session timelines the Users and Live
 * sessions tabs show. Every finding links back to that source, so a verdict is
 * always checkable against what actually happened.
 *
 * The finding id (`rv_20261102_03`) is the handle for the debugging agent:
 * copy it, hand it over, and the agent loads the finding's evidence from there.
 *
 * /reviews is the table of contents — one row per day, newest first — and
 * /reviews/{day} is that day's report (the link the Chat summary posts).
 *
 * Production only. A dev run writes its own report, but there is no reason to
 * triage dev test traffic here.
 */

/** How far back the table of contents reaches (the engine's cap). */
const INDEX_DAYS = 60;

const ENV = "production" as const;

const SEVERITIES: ReviewSeverity[] = ["high", "medium", "low"];

const AREAS: { key: ReviewArea; label: string }[] = [
  { key: "broken_flow", label: "Broken flow" },
  { key: "reply_quality", label: "Atula replies" },
  { key: "skill_quality", label: "Skill outputs" },
  { key: "live_conversation", label: "Live conversation" },
];

const AREA_LABEL = Object.fromEntries(AREAS.map((a) => [a.key, a.label])) as Record<
  ReviewArea,
  string
>;

function sevTone(s: ReviewSeverity): string {
  return s === "high" ? "badge bad" : s === "medium" ? "badge warn" : "badge";
}

/** "Mon, Nov 2" — PT days are calendar dates, so no timezone shift applies. */
function fmtDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function DayIndex({ days }: { days: ReviewReport[] }) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Day</th>
            <th>Reviewed</th>
            <th>Findings</th>
            <th>High</th>
            <th>Medium</th>
            <th>Low</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const c = d.counts;
            const done = d.status === "complete" && c;
            const high = c?.by_severity?.high ?? 0;
            return (
              <tr key={d.day} className={done && high > 0 ? "row-warn" : undefined}>
                <td className="nowrap">
                  <button
                    className="linklike"
                    onClick={() => navigate({ tab: "reviews", day: d.day })}
                  >
                    {fmtDay(d.day)}
                  </button>
                  <div className="muted small">{d.day}</div>
                </td>
                <td className="small">
                  {done ? `${c.threads} threads · ${c.sessions} live` : "—"}
                </td>
                <td>{done ? c.findings : "—"}</td>
                <td>
                  {done ? (
                    high > 0 ? <span className="badge bad">{high}</span> : 0
                  ) : (
                    "—"
                  )}
                </td>
                <td>{done ? (c.by_severity?.medium ?? 0) : "—"}</td>
                <td>{done ? (c.by_severity?.low ?? 0) : "—"}</td>
                <td className="small">
                  {d.status === "complete"
                    ? d.chat_posted
                      ? "Posted to Chat"
                      : "Complete"
                    : d.status === "failed"
                      ? <span className="badge bad">failed</span>
                      : d.status}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FindingDetail({ f }: { f: ReviewFinding }) {
  return (
    <tr className="detail">
      <td colSpan={5}>
        <dl className="kv">
          <dt>What happened</dt>
          <dd>{f.what_happened}</dd>
          {f.quote && (
            <>
              <dt>Quote</dt>
              <dd>
                <q>{f.quote}</q>
                {f.when && <span className="muted small"> · {f.when}</span>}
              </dd>
            </>
          )}
          <dt>Why it matters</dt>
          <dd>{f.why_it_matters}</dd>
          <dt>Likely cause</dt>
          <dd className="muted">{f.likely_cause || "unclear"}</dd>
          <dt>Source</dt>
          <dd className="small">
            <button
              className="linklike"
              onClick={() => navigate({ tab: "users", q: f.user_id })}
            >
              Open thread ({f.user_id})
            </button>
            {f.msid && (
              <>
                {" · "}
                <button
                  className="linklike"
                  onClick={() => navigate({ tab: "sessions", msid: f.msid })}
                >
                  Open session ({f.msid}
                  {f.session_type ? `, ${f.session_type}` : ""})
                </button>
              </>
            )}
          </dd>
          <dt>Finding id</dt>
          <dd className="small">
            <code>{f.id}</code>{" "}
            <CopyLink
              route={{ tab: "reviews", day: f.day, finding: f.id }}
              label="Copy link"
            />
          </dd>
        </dl>
      </td>
    </tr>
  );
}

export function DailyReview() {
  const route = useRoute();
  const [days, setDays] = useState<ReviewReport[] | null>(null);
  const [report, setReport] = useState<ReviewReport | null>(null);
  const [severity, setSeverity] = useState<ReviewSeverity | "all">("all");
  const [area, setArea] = useState<ReviewArea | "all">("all");
  const [open, setOpen] = useState<string>(route.finding);
  const [showItems, setShowItems] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // The day list is small (one row per day) and changes once a day; load it once.
  useEffect(() => {
    api
      .listReviews(ENV, INDEX_DAYS)
      .then((p) => setDays(p.items))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  // No day in the URL means the table of contents.
  const day = route.day;

  useEffect(() => {
    if (!day) {
      setReport(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setReport(null);
    api
      .review(day, ENV)
      .then((r) => {
        if (cancelled) return;
        setReport(r);
        setError("");
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [day]);

  // A shared ?finding= link opens that row and scrolls to it.
  useEffect(() => {
    if (!route.finding || !report) return;
    setOpen(route.finding);
    setSeverity("all");
    setArea("all");
    requestAnimationFrame(() =>
      document.getElementById(route.finding)?.scrollIntoView({ block: "center" }),
    );
  }, [route.finding, report]);

  const findings = report?.findings ?? [];
  const visible = useMemo(
    () =>
      findings.filter(
        (f) =>
          (severity === "all" || f.severity === severity) &&
          (area === "all" || f.area === area),
      ),
    [findings, severity, area],
  );
  const counts = report?.counts;
  // Neighbours in the list (newest first), for previous/next-day links.
  const at = days?.findIndex((d) => d.day === day) ?? -1;
  const newer = at > 0 ? days?.[at - 1]?.day : undefined;
  const older = at >= 0 ? days?.[at + 1]?.day : undefined;

  return (
    <section>
      <header className="section-head">
        <h2>Daily review</h2>
      </header>

      <p className="muted small">
        Each morning an AI reviewer reads the previous day's production iMessage
        threads and live sessions (Pacific time) and flags what went wrong. It
        can be wrong, so check each finding against its source. To root-cause
        one, give its id to the debugging agent.
      </p>

      {day && (
        <p className="small">
          <button className="linklike" onClick={() => navigate({ tab: "reviews" })}>
            ← All days
          </button>
          {older && (
            <>
              {" · "}
              <button
                className="linklike"
                onClick={() => navigate({ tab: "reviews", day: older })}
              >
                ‹ {fmtDay(older)}
              </button>
            </>
          )}
          {newer && (
            <>
              {" · "}
              <button
                className="linklike"
                onClick={() => navigate({ tab: "reviews", day: newer })}
              >
                {fmtDay(newer)} ›
              </button>
            </>
          )}
        </p>
      )}

      {!day && days && days.length > 0 && <DayIndex days={days} />}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!day && days && days.length === 0 && !error && (
        <p className="muted empty">
          No reports yet. The daily job writes one each morning; run{" "}
          <code>scripts/daily_review.py</code> to create one now.
        </p>
      )}

      {loading && day && <p className="muted">Loading {fmtDay(day)}…</p>}

      {report && report.status === "failed" && (
        <p className="error">
          The review for {fmtDay(report.day)} failed: {report.error || "unknown error"}.
          Re-run it with <code>scripts/daily_review.py --day {report.day}</code>.
        </p>
      )}
      {report && report.status === "running" && (
        <p className="muted">The review for {fmtDay(report.day)} is still running.</p>
      )}

      {report && counts && (
        <>
          <h3>{fmtDay(report.day)}</h3>
          <div className="tiles">
            <div className="tile">
              <div className="tile-label">Reviewed</div>
              <div className="tile-value">{counts.threads + counts.sessions}</div>
              <div className="tile-sub">
                {counts.threads} threads · {counts.sessions} live sessions
              </div>
            </div>
            {SEVERITIES.map((s) => (
              <div
                key={s}
                className={s === "high" && (counts.by_severity[s] ?? 0) > 0 ? "tile tile-bad" : "tile"}
              >
                <div className="tile-label">{s}</div>
                <div className="tile-value">{counts.by_severity[s] ?? 0}</div>
              </div>
            ))}
            {counts.failed_items > 0 && (
              <div className="tile">
                <div className="tile-label">Not reviewed</div>
                <div className="tile-value">{counts.failed_items}</div>
                <div className="tile-sub">failed to load or review</div>
              </div>
            )}
          </div>

          <div className="filters">
            <div className="tabs">
              <span className="filter-label">Severity</span>
              {(["all", ...SEVERITIES] as const).map((s) => (
                <button
                  key={s}
                  className={severity === s ? "tab active" : "tab"}
                  onClick={() => setSeverity(s)}
                >
                  {s === "all" ? "All" : s}
                  <span className="count">
                    {s === "all" ? counts.findings : (counts.by_severity[s] ?? 0)}
                  </span>
                </button>
              ))}
            </div>
            <div className="tabs">
              <span className="filter-label">Area</span>
              <button
                className={area === "all" ? "tab active" : "tab"}
                onClick={() => setArea("all")}
              >
                All
              </button>
              {AREAS.map((a) => (
                <button
                  key={a.key}
                  className={area === a.key ? "tab active" : "tab"}
                  onClick={() => setArea(a.key)}
                >
                  {a.label}
                  <span className="count">{counts.by_area[a.key] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>

          {findings.length === 0 && (
            <p className="muted empty">No issues found on {fmtDay(report.day)}. ✅</p>
          )}
          {findings.length > 0 && visible.length === 0 && (
            <p className="muted empty">No finding matches these filters.</p>
          )}

          {visible.length > 0 && (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Area</th>
                    <th>Finding</th>
                    <th>Where</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((f) => {
                    const isOpen = open === f.id;
                    return [
                      <tr
                        key={f.id}
                        id={f.id}
                        className={route.finding === f.id ? "row-highlight" : undefined}
                      >
                        <td className="nowrap">
                          <span className={sevTone(f.severity)}>{f.severity}</span>
                        </td>
                        <td className="nowrap small">{AREA_LABEL[f.area] ?? f.area}</td>
                        <td>
                          {f.title}
                          <div className="muted small">
                            <code>{f.id}</code>
                          </div>
                        </td>
                        <td className="small">
                          {f.item_kind === "live" ? "Live" : "iMessage"}
                          <div className="muted small">{f.msid || f.user_id}</div>
                        </td>
                        <td>
                          <button
                            className="ghost small"
                            onClick={() => setOpen(isOpen ? "" : f.id)}
                          >
                            {isOpen ? "Hide" : "Open"}
                          </button>
                        </td>
                      </tr>,
                      isOpen ? <FindingDetail key={`${f.id}-detail`} f={f} /> : null,
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Every reviewed item with the reviewer's one-line summary, so a clean
              day is visibly "looked at N conversations", not just an empty table. */}
          <p className="small">
            <button className="ghost small" onClick={() => setShowItems((v) => !v)}>
              {showItems ? "Hide" : "Show"} all {report.items?.length ?? 0} reviewed
              conversations
            </button>
          </p>
          {showItems && (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Conversation</th>
                    <th>Findings</th>
                    <th>Reviewer summary</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.items ?? []).map((it) => (
                    <tr key={it.key} className={it.error ? "row-warn" : undefined}>
                      <td className="small nowrap">
                        <button
                          className="linklike"
                          onClick={() =>
                            it.kind === "live"
                              ? navigate({ tab: "sessions", msid: it.msid })
                              : navigate({ tab: "users", q: it.user_id })
                          }
                        >
                          {it.kind === "live"
                            ? `Live · ${it.session_type || "session"} · ${it.msid}`
                            : `iMessage · ${it.user_id}`}
                        </button>
                      </td>
                      <td className="small">{it.findings}</td>
                      <td className="small">
                        {it.error ? `Not reviewed (${it.error})` : it.summary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="muted small">
            {report.model && `Reviewed by ${report.model}`}
            {report.rubric_version && ` · rubric ${report.rubric_version}`}
            {report.chat_posted !== undefined &&
              ` · Chat summary ${report.chat_posted ? "posted" : "not posted"}`}
          </p>
        </>
      )}
    </section>
  );
}
